import {
  KeyboardEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from "react";
import { cn } from "web-design-system";
import { LockKeyhole } from "lucide-react";
import type { ProductBreakdownItem as BaseProductBreakdownItem } from "creso-core/types";
import { formatAsCrore, formatAsPercentage } from "creso-core/utils";
import { ValueVector } from "../primitives";

export type ProductBreakdownItem = BaseProductBreakdownItem & {
  status?: "active" | "locked" | "empty";
  statusLabel?: string | null;
  action?: ReactElement;
};

export function ProductCard({
  product,
  icon,
  className,
  onClick,
  children,
}: PropsWithChildren<{
  product: ProductBreakdownItem;
  /** Optional leading icon, rendered before the label block. */
  icon?: ReactNode;
  className?: string;
  /** Makes the whole card clickable. */
  onClick?: () => void;
}>) {
  const isLocked = product.status === "locked";
  const showValue = product.currentValue != null || !isLocked;
  const hasAction = Boolean(children);

  return (
    <div
      className={cn(
        "relative flex h-20 rounded-lg bg-background border border-border justify-between flex-1 p-4",
        hasAction && "group/product",
        onClick && "cursor-pointer transition-colors hover:bg-muted",
        className
      )}
      {...(onClick && {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        },
      })}
    >
      {icon ? (
        <div className="size-11 rounded-lg bg-secondary flex items-center justify-center shrink-0 self-center mr-3">
          {icon}
        </div>
      ) : null}
      <div className="flex flex-col gap-1 text-sm min-w-0 mr-auto justify-center">
        <span className="font-semibold truncate">{product.label}</span>
        <span className="text-muted-foreground truncate">
          {product.statusLabel ??
            (product.allocationPct != null
              ? `${formatAsPercentage(product.allocationPct, "-", 2)} allocation`
              : product.subText)}
        </span>
      </div>
      {showValue ? (
        <div
          className={cn(
            "flex flex-col items-end text-sm gap-1 shrink-0 justify-center",
            hasAction && "group-hover/product:hidden"
          )}
        >
          <span className="font-semibold">
            {formatAsCrore(product.currentValue ?? "")}
          </span>
          {product.growthPct != null && !isLocked ? (
            <ValueVector value={product.growthPct} />
          ) : null}
        </div>
      ) : null}
      {isLocked ? (
        <div className="absolute right-4 top-5 border border-border rounded-lg p-2 bg-muted">
          <LockKeyhole className="size-6" />
        </div>
      ) : null}
      {hasAction ? (
        <div className="self-center hidden group-hover/product:flex">
          {children}
        </div>
      ) : null}
    </div>
  );
}
